import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@stoneage/App/Components/Notify";
import ProductCarouselComponent from "@stoneage/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaRelatedProductsEvent } from "@stoneage/App/Utilities/DataLayer";
import { hasRole } from "@stoneage/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    showImage = "showImage",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showTitle = "showTitle",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    visibleElementsTitle = "visibleElementsTitle",
    titleAlignment = "titleAlignment",
    customCSS = "customCSS",
}

export interface ProductCarouselFields {
    [fields.title]: string;
    [fields.carouselType]: string;
    [fields.displayProductsFrom]: string;
    [fields.selectedCategoryIds]: string[];
    [fields.relatedProductType]: string;
    [fields.numberOfProductsToDisplay]: number;
    [fields.seedWithManuallyAssigned]: string;
    [fields.showImage]: boolean;
    [fields.showPartNumbers]: boolean;
    [fields.showPrice]: boolean;
    [fields.showTitle]: boolean;
    [fields.showAvailability]: boolean;
    [fields.showAddToCart]: boolean;
    [fields.visibleElementsTitle]: string;
    [fields.titleAlignment]: string;
    [fields.customCSS]: string;
}

interface OwnProps extends WidgetProps {
    [x: string]: any;
    fields: ProductCarouselFields;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const userRoles = getSession(state).userRoles;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        allowedProductRelations: (getSettingsCollection(state) as any)?.customSettings["relatedProductTypes"]?.split(
            ",",
        ),
        session: state.context.session,
        // XNG-Changes: Check if the user has TrainingOnly Role
        isTrainingOnly: hasRole(userRoles, "TrainingOnly", true),
        impersonation: trackAsImpersonation(state),
        customerNumber: getBillToCustomerNumber(state),
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
}

export const productCarouselStyles: ProductCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        color: "common.black",
    },
    carouselWrapper: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.titleText {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const styles = productCarouselStyles;

const ProductCarousel: React.FC<Props> = ({
    id,
    fields: {
        title,
        carouselType,
        displayProductsFrom,
        selectedCategoryIds,
        relatedProductType,
        numberOfProductsToDisplay,
        seedWithManuallyAssigned,
        showImage,
        showPartNumbers,
        showPrice,
        showTitle,
        showAvailability,
        showAddToCart,
        visibleElementsTitle,
        titleAlignment,
        customCSS,
    },
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    allowedProductRelations,
    session,
    // XNG-Changes: Check if the user is a training only role
    isTrainingOnly,
    impersonation,
    customerNumber,
}) => {
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const dispatch = useDispatch();

    React.useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType,
            relatedProductType,
            seedWithManuallyAssigned,
            displayProductsFrom,
            selectedCategoryIds,
            numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        carouselType,
        relatedProductType,
        seedWithManuallyAssigned,
        displayProductsFrom,
        selectedCategoryIds,
        numberOfProductsToDisplay,
        productContext?.product?.id,
        category?.id,
        brand?.id,
        session?.pickUpWarehouse?.id,
    ]);

    React.useEffect(() => {
        if (products && products.length && isProductDetailsPage) {
            if (carouselType === "relatedProducts" && allowedProductRelations.includes(relatedProductType)) {
                dispatch({
                    type: "xCommon/update",
                    data: {
                        key: relatedProductType,
                        value: true,
                    },
                });
            }
        }
    }, [products, productContext?.product?.id]);

    if (errorMessage && !products) {
        return (
            <Notify
                messageType="danger"
                body={`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            />
        );
    }

    if (!products || products.length === 0) {
        if (carouselType === "relatedProducts" && allowedProductRelations.includes(relatedProductType)) {
            dispatch({
                type: "xCommon/update",
                data: {
                    key: relatedProductType,
                    value: false,
                },
            });
        }
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };

    // XNG-Changes: Track GA4
    const relatedProductsEventHandler = (destination: string) => {
        if (carouselType === "relatedProducts" && productContext?.product.productNumber) {
            trackGaRelatedProductsEvent(productContext.product.productNumber, destination, impersonation, customerNumber)
        }
    };

    return (
        <StyledWrapper {...styles.carouselWrapper} {...customCssWrapper} id={`productCarousel_${carouselType}_${relatedProductType}`}>
            <React.Suspense fallback={<LoadingOverlay loading={true} />}>
                {title &&
                    <ContentWrapper alignment={titleAlignment} className="wrapper">
                        <Typography {...styles.titleText}>{parse(title, parserOptions)}</Typography>
                    </ContentWrapper>
                }
                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                    <ProductCarouselComponent
                        id={id}
                        products={products}
                        fields={{
                            showImage,
                            showPartNumbers: true,
                            showPrice: showPrice && !isTrainingOnly,
                            showTitle,
                            showAvailability: showAvailability && !isTrainingOnly,
                            showAddToCart,
                            showAttributes: true,
                            showCompare: false,
                        }}
                        imageDimensions={308}
                        relatedProductsEventHandler={relatedProductsEventHandler}
                    />
                </StyledWrapper>
            </React.Suspense>
        </StyledWrapper>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
];

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(ProductCarousel)),
    definition: {
        group: "XNGAGE" as any,
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "flex-start",
                horizontal: true,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 2,
                tab: basicTab,
                customFilter: (item, page, settings) => {
                    return (
                        settings.settingsCollection.websiteSettings.enableDynamicRecommendations ||
                        item.value !== "topSellers"
                    );
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 3,
                tab: basicTab,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 4,
                tab: basicTab,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 6,
                tab: basicTab,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                    pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 7,
                tab: basicTab,
            },
            {
                name: fields.showImage,
                displayName: "Product Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 8,
                tab: basicTab,
            },
            {
                name: fields.visibleElementsTitle,
                displayName: "Visible Elements",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 9,
                tab: basicTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 10,
                tab: basicTab,
            },
            {
                name: fields.showAvailability,
                displayName: "Inventory",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 11,
                tab: basicTab,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 12,
                tab: basicTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "CTA",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 13,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
